<template>
  <div>
    <component :is="'style'">{{ activeBrandCss }}</component>

    <div class="pg">
      <aside class="pg__sidebar">
        <div class="pg__sidebar-header">
          <NuxtLink class="pg__logo" :to="withBrand('/figma-playground')">Figma DS</NuxtLink>
          <select v-model="brand" class="pg__brand-select">
            <option v-for="name in brands" :key="name" :value="name">{{ name.toUpperCase() }}</option>
          </select>
        </div>

        <nav class="pg__nav">
          <h2 class="pg__nav-title">Brand</h2>
          <NuxtLink
            v-for="page in brandPages"
            :key="page.path"
            class="pg__nav-link"
            active-class="pg__nav-link--active"
            :to="withBrand(page.path)"
          >
            {{ page.name }}
            <span v-if="page.needsKit && !hasKit" class="pg__nav-flag" title="No brand kit for this brand">—</span>
          </NuxtLink>

          <h2 class="pg__nav-title pg__nav-title--spaced">Components</h2>
          <NuxtLink
            v-for="story in stories"
            :key="story.slug"
            class="pg__nav-link"
            active-class="pg__nav-link--active"
            :to="withBrand(`/figma-playground/components/${story.slug}`)"
          >
            {{ story.name }}
          </NuxtLink>
        </nav>
      </aside>

      <main class="pg__main">
        <NuxtPage />
      </main>
    </div>
  </div>
</template>

<script setup lang="ts">
  import '~/assets/styles/reset.css';
  import '@tapp/design-tokens/figma-variables/base.css';
  // Generated text-style utility classes (.ds-h1-strong, .ds-body-large-regular, …);
  // values flow from the --typography-* tokens above, so they follow brand switches.
  import '@tapp/design-tokens/figma-variables/typography.css';

  import { brands, isBrand, loadBrandCss, DEFAULT_BRAND } from '../figma-playground/brands';
  import { brandsWithKit } from '../figma-playground/brand-kits';
  import { stories } from '../figma-playground/stories';

  const brandPages = [
    { path: '/figma-playground/brand', name: 'Brand details', needsKit: true },
    { path: '/figma-playground/colours', name: 'Brand colours', needsKit: false },
    { path: '/figma-playground/typography', name: 'Brand typography', needsKit: false },
  ];

  const route = useRoute();
  const router = useRouter();

  // The brand lives in the query string so a link to a component carries the theme it was
  // shared in. An unknown or missing ?brand= falls back instead of rendering unthemed.
  const brand = computed({
    get: () => (isBrand(route.query.brand) ? route.query.brand : DEFAULT_BRAND),
    set: (name: string) => router.replace({ query: { ...route.query, brand: name } }),
  });

  // Colours and typography still have tokens to show without a kit; brand details does not.
  const hasKit = computed(() => brandsWithKit.has(brand.value));

  function withBrand(path: string) {
    return { path, query: { brand: brand.value } };
  }

  const activeBrandCss = ref(await loadBrandCss(brand.value));

  // Loading a theme is async, so running through the switcher can resolve out of order.
  watch(brand, async (name) => {
    const css = await loadBrandCss(name);
    if (brand.value === name) activeBrandCss.value = css;
  });
</script>

<style lang="postcss" scoped>
  .pg {
    display: grid;
    grid-template-columns: 15rem 1fr;

    min-height: 100vh;

    font-family: Arial, sans-serif;
    color: #1a1a1a;
  }

  .pg__sidebar {
    position: sticky;
    top: 0;

    overflow-y: auto;
    display: flex;
    flex-direction: column;

    height: 100vh;
    padding: 1.5rem 1rem;
    border-right: 1px solid #e4e4e4;

    background: #fafafa;
  }

  .pg__sidebar-header {
    display: flex;
    flex-direction: column;
    gap: 0.75rem;

    margin-bottom: 1.5rem;
    padding-bottom: 1rem;
    border-bottom: 1px solid #e4e4e4;
  }

  .pg__logo {
    font-size: 1.125rem;
    font-weight: 700;
    color: inherit;
    text-decoration: none;
    letter-spacing: -0.02em;
  }

  .pg__brand-select {
    cursor: pointer;

    width: 100%;
    padding: 0.375rem 0.5rem;
    border: 1px solid #d8d8d8;
    border-radius: 0.25rem;

    font-size: 0.75rem;
    font-weight: 700;

    background: #fff;
  }

  .pg__nav {
    display: flex;
    flex-direction: column;
    gap: 0.125rem;
  }

  .pg__nav-title {
    margin: 0 0 0.5rem;

    font-size: 0.625rem;
    font-weight: 700;
    color: #929292;
    text-transform: uppercase;
    letter-spacing: 0.08em;
  }

  .pg__nav-title--spaced {
    margin-top: 1.25rem;
  }

  .pg__nav-flag {
    margin-left: 0.25rem;
    color: #b8b8b8;
  }

  .pg__nav-link {
    padding: 0.375rem 0.625rem;
    border-radius: 0.25rem;

    font-size: 0.8125rem;
    color: #545454;
    text-decoration: none;

    transition:
      background-color 0.15s,
      color 0.15s;

    &:hover {
      color: #1a1a1a;
      background: #f2f2f2;
    }
  }

  .pg__nav-link--active {
    font-weight: 600;
    color: #1a1a1a;
    background: #e4e4e4;
  }

  .pg__main {
    padding: 2rem 3rem 6rem;
    background: #fff;
  }
</style>
